import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import FileUpload from "@/components/FileUpload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function QuoteForm({ defaultCategory = "demande-de-devis", defaultSubject = "", lockCategory = false }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cats, setCats] = useState([]);
  const [fileIds, setFileIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ category: defaultCategory, subject: defaultSubject, description: "", quantity: 1, phone: "" });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => { api.get("/categories").then(({ data }) => setCats(data)).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Connectez-vous pour envoyer une demande"); navigate("/connexion"); return; }
    if (!form.subject || !form.description) { toast.error("Renseignez le sujet et la description"); return; }
    setLoading(true);
    try {
      await api.post("/quotes", { ...form, quantity: Number(form.quantity) || 1, file_ids: fileIds });
      setDone(true);
      toast.success("Demande envoyée ! Nous revenons vers vous rapidement.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-2xl border border-[#0055FF]/40 bg-[#0055FF]/10 p-10 text-center">
        <CheckCircle2 className="w-14 h-14 mx-auto text-[#0055FF]" />
        <h3 className="mt-5 font-heading text-2xl font-bold">Demande envoyée !</h3>
        <p className="mt-3 text-gray-300">Merci, nous avons bien reçu votre demande de devis. Un e-mail de confirmation vous a été envoyé.</p>
        <div className="mt-7 flex justify-center gap-3">
          <Button onClick={() => navigate("/compte")} className="btn-primary rounded-full px-6 font-bold">Voir mes devis</Button>
          <Button onClick={() => { setDone(false); setForm({ category: defaultCategory, subject: defaultSubject, description: "", quantity: 1, phone: "" }); setFileIds([]); }} variant="ghost" className="rounded-full">Nouvelle demande</Button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-2xl border border-[#222] bg-[#0A0A0A] p-6 sm:p-8 space-y-5" data-testid="quote-form">
      {!lockCategory && (
        <div>
          <Label>Catégorie</Label>
          <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
            <SelectTrigger data-testid="quote-category" className="bg-black border-white/15 mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              {cats.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
      <div className="grid gap-5 sm:grid-cols-2">
        <div><Label>Sujet</Label><Input data-testid="quote-subject" value={form.subject} onChange={set("subject")} placeholder="Ex: Lithophanie photo de famille" className="bg-black border-white/15 mt-1" /></div>
        <div><Label>Quantité</Label><Input data-testid="quote-quantity" type="number" min="1" value={form.quantity} onChange={set("quantity")} className="bg-black border-white/15 mt-1" /></div>
      </div>
      <div><Label>Téléphone (optionnel)</Label><Input data-testid="quote-phone" value={form.phone} onChange={set("phone")} className="bg-black border-white/15 mt-1" /></div>
      <div><Label>Description du projet</Label><Textarea data-testid="quote-description" value={form.description} onChange={set("description")} rows={5} placeholder="Décrivez votre projet : dimensions, couleurs, matière, usage..." className="bg-black border-white/15 mt-1" /></div>
      <div>
        <Label className="mb-2 block">Vos fichiers (STL, 3MF, OBJ, STEP, ZIP, images)</Label>
        <FileUpload onChange={setFileIds} />
      </div>
      <Button data-testid="quote-submit" type="submit" disabled={loading} className="btn-primary w-full rounded-full py-6 text-base font-bold">
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Send className="w-4 h-4 mr-2" /> Envoyer ma demande</>}
      </Button>
      {!user && <p className="text-xs text-center text-gray-500">Connexion requise pour envoyer une demande et joindre des fichiers.</p>}
    </form>
  );
}
